"use client";

import { UIEventHandler } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/modules/common/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modules/common/components/dropdown-menu";
import { cn } from "@/lib/util/utils";
import { useNotifications } from "@/modules/notifications/hooks/use-notifications";

function getRelativeTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "Hace un momento";

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `Hace ${diffInHours} h`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `Hace ${diffInDays} d`;

  return date.toLocaleDateString();
}

export function NotificationMenu() {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    loadMore,
    hasMore,
    isLoadingMore,
  } = useNotifications();

  // Función para detectar si el usuario hizo scroll hasta el final del menú
  const handleScroll: UIEventHandler<HTMLDivElement> = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    // Si estamos a menos de 50px del final de la lista, cargamos más
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (hasMore && !isLoadingMore) {
        loadMore();
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-green-main hover:bg-green-main/10 relative flex focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="bg-orange-main absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-cream ml-12 w-80 sm:ml-0">
        <div className="flex items-center justify-between">
          <DropdownMenuLabel className="text-green-main font-bold">
            Notificaciones
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead()}
              className="text-green-main/70 hover:text-green-main mr-2 text-[10px] font-medium"
            >
              Marcar todas como leídas
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="bg-green-main/10" />
        <div className="max-h-75 overflow-y-auto" onScroll={handleScroll}>
          {notifications.length > 0 ? (
            <>
              {notifications.map((notification) => {
                const isUnread = !notification.read_at;
                return (
                  <DropdownMenuItem
                    key={notification.id}
                    onClick={() => {
                      if (isUnread) markAsRead(notification.id);
                    }}
                    className="focus:bg-green-main/5 flex cursor-pointer flex-col items-start gap-1 p-3 transition-colors"
                  >
                    <div className="flex w-full items-start justify-between gap-2">
                      <span
                        className={cn(
                          "text-sm font-semibold",
                          isUnread ? "text-green-main" : "text-gray-700"
                        )}
                      >
                        {notification.title}
                      </span>
                      {isUnread && (
                        <span className="bg-orange-main mt-1 h-2 w-2 shrink-0 rounded-full" />
                      )}
                    </div>
                    <p className="line-clamp-2 text-xs text-gray-500">
                      {notification.message}
                    </p>
                    <span className="mt-1 text-[10px] text-gray-400">
                      {getRelativeTime(notification.created_at)}
                    </span>
                  </DropdownMenuItem>
                );
              })}

              {/* Spinner de Carga (Infinite Scroll) */}
              {isLoadingMore && (
                <div className="flex items-center justify-center p-4">
                  <span className="border-green-main h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-center text-sm text-gray-500">
              No tienes notificaciones
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
